import * as React from 'react';

import {FormField} from 'shells/form/form-field';
import {CopyableLink} from 'shells/link/copyable-link';
import {TextField} from 'shells/textfield/textfield';
import {useDebounce} from 'shells/utils/hooks/use-debounce';
import {useDidMountEffect} from 'shells/utils/hooks/use-did-mount-effect';

import type {EditionUpdateInput, EditionFragment as Edition} from 'nutshell-graphql-types';

import styles from './drip-editions-details-form.module.css';

type Props = {
    edition: Edition;
    onEditionSave: (saveData: EditionUpdateInput) => Promise<any>;
    shouldReload?: boolean;
    setShouldReload?: (arg1: boolean) => void;
    isReadOnly: boolean;
};

export function DripEditionsDetailsForm(props: Props) {
    const [editionId, setEditionId] = React.useState(props.edition.id);
    const [shouldSave, setShouldSave] = React.useState<boolean>(true);

    const [subject, setSubject] = React.useState<string>(
        props.edition.emailTemplate ? props.edition.emailTemplate.subject : ''
    );
    const debouncedSubject = useDebounce(subject, 300);

    const [previewText, setPreviewText] = React.useState<string>(
        props.edition.emailTemplate ? props.edition.emailTemplate.previewText : ''
    );
    const debouncedPreviewText = useDebounce(previewText, 300);

    const setShouldReload = props.setShouldReload;

    React.useEffect(() => {
        // If the edition has changed, or changes have been discarded
        if (props.edition.id !== editionId || props.shouldReload) {
            setSubject(props.edition.emailTemplate ? props.edition.emailTemplate.subject : '');
            setPreviewText(
                props.edition.emailTemplate ? props.edition.emailTemplate.previewText : ''
            );

            setEditionId(props.edition.id);

            if (setShouldReload) {
                setShouldReload(false);
            }

            // When the subject and previewText states are updated a save will be triggered - which we don't want
            setShouldSave(false);
        }
    }, [
        props.edition.id,
        editionId,
        props.edition.emailTemplate,
        props.shouldReload,
        setShouldReload,
    ]);

    useDidMountEffect(() => {
        // When fields are edited shouldSave will be true
        if (shouldSave) {
            props.onEditionSave({
                editionId: editionId,
                subject: debouncedSubject.length ? debouncedSubject : null,
                previewText: debouncedPreviewText.length ? debouncedPreviewText : null,
            });
        }
    }, [debouncedSubject, debouncedPreviewText]);

    return (
        <form
            onSubmit={(event) => {
                event.preventDefault();
            }}
            className={styles['drip-editions-details-form']}
        >
            <div>
                {/* For active drips, we want the form to display view only information */}
                {props.isReadOnly ? (
                    <>
                        <FormField innerId='subject' label='Subject'>
                            <div id='subject'>{subject}</div>
                        </FormField>
                        <FormField innerId='previewText' label='Preview text'>
                            <div id='previewText'>
                                {previewText ? (
                                    previewText
                                ) : (
                                    <span className={styles['empty-field']}>No preview text</span>
                                )}
                            </div>
                        </FormField>
                    </>
                ) : (
                    <>
                        <FormField innerId='subject' label='Subject' isRequired={true}>
                            <TextField
                                id='subject'
                                // @ts-expect-error
                                name='subject'
                                placeholder='No subject'
                                value={subject}
                                onChange={(value) => {
                                    if (!shouldSave) {
                                        setShouldSave(true);
                                    }
                                    setSubject(value);
                                }}
                            />
                        </FormField>
                        <FormField
                            innerId='previewText'
                            label='Preview text'
                            helperText='Appears after the subject line in a recipient’s inbox'
                        >
                            <TextField
                                id='previewText'
                                // @ts-expect-error
                                name='preview text'
                                placeholder='Short blurb about your email'
                                value={previewText}
                                onChange={(value) => {
                                    if (!shouldSave) {
                                        setShouldSave(true);
                                    }
                                    setPreviewText(value);
                                }}
                            />
                        </FormField>
                    </>
                )}
            </div>
            <FormField
                noMargin={true}
                tooltipInfo={
                    <div>
                        Use this public link to share your message anywhere: on social media, your
                        website, or just among teammates.
                    </div>
                }
                innerId='shareableUrl'
                label='Shareable URL'
            >
                <CopyableLink
                    size='big'
                    name='ShareableUrl'
                    value={props.edition.viewInBrowserLink}
                />
            </FormField>
        </form>
    );
}
